import { resolveDesktopModuleDatefieldDateFilterParameter } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';

import { DateRangeSelection } from 'src/models/common';
import { GenericActivityListPagination, GenericActivityListSort } from 'src/models/genericActivityList';
import {
	MAIL_EXTENDED_SEARCH_BACKEND_KEYS,
	MailExtendedSearchFieldName,
	MailExtendedSearchValues,
} from 'src/models/mailList/extendedSearch';
import { AbstractQuery } from 'src/models/query';

export class GetTheMailModuleBoxesForTheUserQuery extends AbstractQuery {
	name = 'IFS_DesktopModule_Mail_Query_Queries_GetTheMailModuleBoxesForTheUserQuery';
	page = 1;
	subscribeForUpdates = false;
}

type MailExtendedSearchAutocompletePayload = {
	triggerAutocompleteField: MailExtendedSearchFieldName;
} & MailExtendedSearchValues;

export class GetTheExtendedSearchAutocompletionsForTheUserQuery extends AbstractQuery {
	cacheable = false;
	ignoreCachedResult = true;
	name = 'IFS_DesktopModule_Mail_Query_Queries_GetTheExtendedSearchAutocompletionsForTheUserQuery';
	sort = [];
	subscribeForUpdates = false;

	constructor(payload: MailExtendedSearchAutocompletePayload) {
		super();

		this.parameters.push({ [MAIL_EXTENDED_SEARCH_BACKEND_KEYS.contact]: payload.contact });
		this.parameters.push({ [MAIL_EXTENDED_SEARCH_BACKEND_KEYS.subject]: payload.subject });
		this.parameters.push({ [MAIL_EXTENDED_SEARCH_BACKEND_KEYS.text]: payload.text });
		this.parameters.push({ [MAIL_EXTENDED_SEARCH_BACKEND_KEYS.filename]: payload.filename });
		this.parameters.push({ [MAIL_EXTENDED_SEARCH_BACKEND_KEYS.fileType]: payload.fileType });
		this.parameters.push({
			triggerAutocompleteField: MAIL_EXTENDED_SEARCH_BACKEND_KEYS[payload.triggerAutocompleteField],
		});
	}
}

export class GetTheMailModuleMailsForTheUserQuery extends AbstractQuery {
	limit = 100;
	name = 'IFS_DesktopModule_Mail_Query_Queries_GetTheMailModuleMailsForTheUserQuery';
	page = 1;
	sort: Array<GenericActivityListSort> = [
		{
			direction: 'DESC',
			property: 'timestamp',
		},
	];
	standardFilterSelection = '';

	constructor(
		view: string,
		date: DateRangeSelection | string,
		boxIdFilter: Array<string>,
		userIdFilter: Array<number>,
		searchTerm: string,
		hiddenSearchTerm: string,
		direction: 'combined' | 'in' | 'out',
		pagination: GenericActivityListPagination,
		sort: GenericActivityListSort,
		suppressBoxCustomCoupling = false
	) {
		super();

		this.page = pagination.page;
		this.limit = pagination.rowsPerPage;
		this.sort = [sort];

		if (view === 'open') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished',
			});
		} else if (view === 'all') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll',
			});
		} else if (view === 'mine') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonMine';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonMine',
			});
		} else if (view === 'custom') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom',
			});
		}

		this.parameters.push({
			DesktopModuleToolbarButtonGlobalizationFilter: true,
		});

		if (
			(searchTerm !== '' && searchTerm !== undefined) ||
			(hiddenSearchTerm !== '' && hiddenSearchTerm !== undefined)
		) {
			this.parameters.push({
				DesktopModuleToolbarTextfieldSearchField: searchTerm ?? '',
			});

			this.parameters.push({
				DesktopModuleToolbarHiddenfieldSearchField: hiddenSearchTerm ?? '',
			});

			this.parameters.push({
				DesktopModuleToolbarDatefieldDateFilter:
					'DesktopModuleToolbarSegmentedButtonTimeRangeSelectionButtonAll',
			});
		} else {
			this.parameters.push({
				DesktopModuleToolbarDatefieldDateFilter: resolveDesktopModuleDatefieldDateFilterParameter(date),
			});
		}

		this.disableSubscriptionsWhileSearchIsActive(searchTerm, hiddenSearchTerm);

		// Task 3.1f: die Box→Custom-Kopplung kollabiert alle drei Ansichts-Slices (Offen/Meine/Alle) auf dieselbe
		// Custom-Query, sobald Boxen ohne User-Filter aktiv sind (Backend: last-wins auf StandardFilterSelectionButton
		// → 8/8/8). Isolierte gespeicherte-Listen-Tabs unterdrücken die Kopplung, damit jede Ansicht view-scoped mit
		// den Listen-Boxen schneidet. Default-Store ('mailList') behält die klassische Custom-Kollaps-Kopplung (byte-
		// identisch). Backend-Schnittmenge nur für taskList wire-verifiziert; übrige Module als analog angenommen.
		if (!suppressBoxCustomCoupling && boxIdFilter.length > 0 && userIdFilter.length == 0) {
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom',
			});
		}

		this.parameters.push({ DesktopModuleCustomFilterCondition: {} });

		switch (direction) {
			case 'in':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionIncoming',
				});
				break;

			case 'out':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionOutgoing',
				});
				break;

			case 'combined':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionBidirectional',
				});
				break;
		}

		this.parameters.push({ DesktopModuleBoxIds: boxIdFilter });

		if (userIdFilter.length > 0) {
			this.parameters.push({
				DesktopModuleToolbarSplitButtonUserFilter: userIdFilter,
			});

			this.parameters.push({
				DesktopModuleToolbarButtonArchivFilter: true,
			});
		} else {
			this.parameters.push({
				DesktopModuleToolbarButtonArchivFilter: false,
			});
		}
	}

	uniqueQueryIdentifier(): string {
		return this.createStableIdentifier({
			standardFilterSelection: this.standardFilterSelection,
		});
	}
}
